import React, { useCallback, useEffect, useState, useMemo } from "react";
import { getDocs, collection, query, where } from "firebase/firestore";
import { Alert, Button, Divider, Loader, Text, Select, TextInput, Title, SegmentedControl, Drawer, Badge, ActionIcon } from "@mantine/core";
import { DateRangePicker } from "@mantine/dates";
import { useMediaQuery } from "@mantine/hooks";
import { ChevronDown, ChevronUp, Filter } from "tabler-icons-react";
import { IconX } from "@tabler/icons-react";
import { DateRangeQuickPills } from "../shared/date-range-quick-pills";

import { db } from "../../firebase";
import {
  useBuildQuery,
  useHandleQueryResult,
  useResetListingState,
  getWeekOldDate,
  isValidDocumentNumber,
  getSingleDayDateRange,
  normalizeUppercaseValue,
  DOCUMENT_NUMBER_ERROR_MESSAGE,
} from "./hooks";
import {
  dateParam,
  enumParam,
  stringParam,
  upperStringParam,
  useUrlOverlayState,
  useUrlState,
} from "../../hooks/useUrlState";

import { DataEntryTable } from "./data-entry-table";
import { ListingConfig } from "./configurations";

export interface DataEntryListingProps {
  companyId: string;
  templateName: string;
  displayConfig: ListingConfig;
  header: string;
}

export function DataEntryListing({
  companyId,
  templateName,
  displayConfig,
  header,
}: DataEntryListingProps) {
  const [documents, setDocuments] = useState<any[]>([]);
  const [doneInitialFetch, setInitialFetched] = useState<boolean>(false);
  const [lastVisibleDoc, setLastVisibleDoc] = useState<any>();
  const [paginationDone, setPaginationDone] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [filterOptions, setFilterOptions] = useState<Record<string, { value: string; label: string }[]>>({});
  const [filterErrors, setFilterErrors] = useState<Record<string, string | undefined>>({});
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState<boolean>(false);
  const [documentNumberDraft, setDocumentNumberDraft] = useState<string>("");

  const { filterConfig, dateField } = displayConfig || {};
  const isMobile = useMediaQuery("(max-width: 768px)");
  const defaultFromDate = useMemo(() => getWeekOldDate(1), []);
  const defaultToDate = useMemo(() => new Date(), []);
  const urlSchema = useMemo(() => {
    const schema: Record<string, any> = {
      from: dateParam(null),
      to: dateParam(null),
      entryId: stringParam(null),
      entryMode: enumParam(["view", "edit"], null),
    };

    if (filterConfig && Array.isArray(filterConfig)) {
      filterConfig.forEach((config) => {
        schema[config.paramName] = config.paramName === "documentNumber"
          ? upperStringParam(null)
          : stringParam(null);
      });
    }

    return schema;
  }, [filterConfig]);
  const [urlState, setUrlState] = useUrlState(urlSchema);
  const {
    selectedId: selectedEntryId,
    mode: entryMode,
    openOverlay,
    closeOverlay,
  } = useUrlOverlayState({
    idKey: "entryId",
    modeKey: "entryMode",
    defaultMode: "view",
    state: urlState,
    setState: setUrlState,
  });
  const hasExplicitDateRange = Boolean(urlState.from || urlState.to);
  const dates = useMemo<[Date | null, Date | null]>(() => {
    if (hasExplicitDateRange) {
      return [urlState.from || null, urlState.to || null];
    }

    return [defaultFromDate, defaultToDate];
  }, [defaultFromDate, defaultToDate, hasExplicitDateRange, urlState.from, urlState.to]);
  const dateRangeKey = `${dates[0] instanceof Date ? dates[0].toISOString() : ""}|${dates[1] instanceof Date ? dates[1].toISOString() : ""}`;
  const documentFilterConfig = useMemo(
    () => filterConfig?.find((config) => config.paramName === "documentNumber") || null,
    [filterConfig]
  );
  const secondaryFilterConfig = useMemo(
    () => (filterConfig || []).filter((config) => config.paramName !== "documentNumber"),
    [filterConfig]
  );

  const filterValues = useMemo(() => {
    const dynamicFilterValues: Record<string, any> = {};
    if (filterConfig && Array.isArray(filterConfig)) {
      filterConfig.forEach((config) => {
        dynamicFilterValues[config.paramName] = urlState[config.paramName] || null;
      });
    }
    return dynamicFilterValues;
  }, [filterConfig, urlState]);
  const filterValuesKey = useMemo(
    () => JSON.stringify(filterValues),
    [filterValues]
  );
  const stableFilterValues = useMemo(
    () => JSON.parse(filterValuesKey),
    [filterValuesKey]
  );
  const documentNumber = filterValues.documentNumber || null;

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (documentNumberDraft.trim() || documentNumber) count++;
    if (secondaryFilterConfig && Array.isArray(secondaryFilterConfig)) {
      secondaryFilterConfig.forEach((cfg) => {
        if (urlState[cfg.paramName]) count++;
      });
    }
    if (hasExplicitDateRange) count++;
    return count;
  }, [documentNumberDraft, documentNumber, secondaryFilterConfig, urlState, hasExplicitDateRange]);
  const stableDates = useMemo(
    () => dates,
    [dateRangeKey]
  );

  useEffect(() => {
    setDocumentNumberDraft(documentNumber || "");
  }, [documentNumber]);

  useEffect(() => {
    if (!isMobile) {
      setMobileFiltersOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!companyId || !filterConfig || !Array.isArray(filterConfig)) {
      setFilterOptions({});
      return;
    }

    const fetchAllOptions = async () => {
      const newOptions: Record<string, { value: string; label: string }[]> = {};
      for (const config of filterConfig) {
        // Skip text input filters (no sourceCollection)
        if (!config.sourceCollection) {
          continue;
        }
        try {
          const optionsCollectionRef = collection(db, config.sourceCollection);
          let q = query(optionsCollectionRef, where("companyId", "==", companyId));
          const snapshot = await getDocs(q);
          const labelField = config.sourceFieldForLabel;
          newOptions[config.paramName] = snapshot.docs.map(doc => {
            const data = doc.data();
            const labelVal = labelField ? data[labelField] : undefined;
            return {
              value: doc.id,
              label: typeof labelVal === "string" ? labelVal : `Unnamed ${doc.id.substring(0, 5)}`,
            };
          }).sort((a, b) => a.label.localeCompare(b.label));
        } catch (error) {
          console.error(`Error fetching options for ${config.label}:`, error);
          newOptions[config.paramName] = [];
        }
      }
      setFilterOptions(newOptions);
    };

    fetchAllOptions();
  }, [companyId, filterConfig]);

  const [pageSize, setPageSize] = useState<number>(20);

  const handlePageSizeChange = (newPageSize: number) => {
    if (newPageSize === pageSize) return;
    setPageSize(newPageSize);
    reset();
  };

  const buildQuery = useBuildQuery({
    db,
    companyId,
    pageSize,
    collectionName: templateName,
    dateField: dateField || "transactionDate",
  });

  const handleResults = useHandleQueryResult({
    pageSize,
    setDocuments,
    setLastVisibleDoc,
    setPaginationDone,
  });

  const reset = useResetListingState({
    setDocuments,
    setInitialFetched,
    setLastVisibleDoc,
    setPaginationDone,
  });

  // Reset data when filters/company/template change, but do not query on invalid document number input
  useEffect(() => {
    if (documentNumber && !isValidDocumentNumber(documentNumber)) {
      return;
    }
    reset();
  }, [documentNumber, filterValuesKey, companyId, templateName, reset]);

  // Effect to handle changes in the DATE filter
  useEffect(() => {
    if (companyId && templateName) {
      if (stableDates && stableDates[0] && (!stableDates[1] || stableDates[0] <= stableDates[1])) {
        reset();
      }
    }
  }, [dateRangeKey, companyId, templateName, reset, stableDates]);

  const loadMoreEntries = useCallback(async (isInitialLoad = false) => {
    setLoading(true);

    const activeFiltersForQuery = stableFilterValues;
    // For a true initial load (after filters change), lastVisibleDoc should be null.
    // buildQuery handles this if its first argument is null.
    const q = buildQuery(isInitialLoad ? null : lastVisibleDoc, stableDates, activeFiltersForQuery, filterConfig);
    const documentSnapshots = await getDocs(q);

    handleResults(documentSnapshots, isInitialLoad);
    if (isInitialLoad) {
      setInitialFetched(true);
    }
    setLoading(false);
  }, [buildQuery, filterConfig, handleResults, lastVisibleDoc, stableDates, stableFilterValues, templateName]);

  useEffect(() => {
    // This effect triggers data loading.
    // It runs if companyId and dates are valid, it's not currently loading,
    // and an initial fetch for the current set of filters hasn't been completed (doneInitialFetch is false).
    if (companyId && stableDates[0] && !loading && !doneInitialFetch) {
      loadMoreEntries(true);
    }
  }, [companyId, dateRangeKey, filterValuesKey, loading, doneInitialFetch, loadMoreEntries, stableDates]);

  const handleFilterChange = (paramName: string, value: string | null) => {
    setUrlState({ [paramName]: value || null }, { replace: true });
    // Clear error for this field when user changes it
    setFilterErrors(prev => ({ ...prev, [paramName]: undefined }));
  };

  const hasValidDocumentNumber = filterValues.documentNumber && isValidDocumentNumber(filterValues.documentNumber);

  const handleDocumentNumberChange = (value: string | null | undefined) => {
    const upperValue = normalizeUppercaseValue(value);
    setDocumentNumberDraft(upperValue);
    const nextUrlState: Record<string, any> = { documentNumber: upperValue || null };

    // Validate and set error state
    if (upperValue && !isValidDocumentNumber(upperValue)) {
      setFilterErrors(prev => ({ ...prev, documentNumber: DOCUMENT_NUMBER_ERROR_MESSAGE }));
    } else {
      setFilterErrors(prev => ({ ...prev, documentNumber: undefined }));
    }

    // Clear other filters when valid document number entered
    if (upperValue && isValidDocumentNumber(upperValue)) {
      Object.keys(filterValues).forEach((key) => {
        if (key !== "documentNumber") {
          nextUrlState[key] = null;
        }
      });
    }

    setUrlState(nextUrlState, { replace: true });
  };

  // Adjust date range to include result's transaction date
  const handleAdjustDateRange = () => {
    if (documents.length === 0) return;

    const resultDateValue = documents[0][dateField || "transactionDate"];
    const alignedDateRange = getSingleDayDateRange(resultDateValue);
    if (!alignedDateRange) return;

    setUrlState(
      {
        from: alignedDateRange[0],
        to: alignedDateRange[1],
      },
      { replace: false }
    );
  };

  const handleDateRangeChange = (range: [Date | null, Date | null] | null) => {
    const [fromDate, toDate] = range || [];

    setUrlState(
      {
        from: fromDate || null,
        to: toDate || null,
      },
      { replace: true }
    );
  };

  const handleResolvedEntry = useCallback(
    (entry: any, options: { skipAutoAlign?: boolean } = {}) => {
      if (!entry || urlState.from || urlState.to || options.skipAutoAlign) return;

      const alignedDateRange = getSingleDayDateRange(entry[dateField || "transactionDate"]);
      if (!alignedDateRange) return;

      setUrlState(
        {
          from: alignedDateRange[0],
          to: alignedDateRange[1],
        },
        { replace: true }
      );
    },
    [dateField, setUrlState, urlState.from, urlState.to]
  );

  const renderDocumentNumberFilter = (showLabel = false) => {
    if (!documentFilterConfig) return null;

    return (
      <TextInput
        key={documentFilterConfig.paramName}
        label={showLabel ? documentFilterConfig.label : undefined}
        placeholder={documentFilterConfig.placeholder || "e.g. T000-000-001"}
        value={documentNumberDraft}
        onChange={(e) => handleDocumentNumberChange(e.currentTarget.value)}
        error={filterErrors.documentNumber}
        style={{ width: isMobile ? "100%" : "170px", minWidth: isMobile ? "100%" : "170px" }}
        size="sm"
        rightSection={
          documentNumberDraft ? (
            <ActionIcon
              size="xs"
              color="gray"
              onClick={() => handleDocumentNumberChange("")}
              aria-label="Clear document number"
            >
              <IconX size={14} />
            </ActionIcon>
          ) : null
        }
      />
    );
  };

  const renderSecondaryFilters = (showLabel = false) => (
    <>
      {secondaryFilterConfig.map((config) => {
        if (!config.sourceCollection) {
          return null;
        }

        return (
          <Select
            key={config.paramName}
            label={showLabel ? config.label : undefined}
            placeholder={`Filter by ${config.label.toLowerCase()}`}
            data={filterOptions[config.paramName] || []}
            value={filterValues[config.paramName] || null}
            onChange={(value) => handleFilterChange(config.paramName, value)}
            clearable
            searchable
            disabled={hasValidDocumentNumber}
            style={{
              width: isMobile ? "100%" : "170px",
              minWidth: isMobile ? "100%" : "170px",
              opacity: hasValidDocumentNumber ? 0.5 : 1,
            }}
            size="sm"
          />
        );
      })}
    </>
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 105px)",
        minHeight: 0,
      }}
    >
      <div style={{ flexShrink: 0 }}>
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "0.75em",
            marginBottom: isMobile ? "0.75em" : "0.5em",
          }}
        >
          <Title
            order={2}
            sx={(theme) => ({
              fontSize: theme.headings.sizes.h4.fontSize,
              fontWeight: 600,
              color: theme.colors.dark[7] || "#1A1B1E",
              letterSpacing: "-0.01em",
              lineHeight: 1.3,
            })}
          >
            {header}
          </Title>

          {!isMobile && (
            <DateRangePicker
              placeholder="Date range"
              aria-label="Date range"
              amountOfMonths={2}
              dropdownPosition="bottom"
              style={{
                width: "210px",
                opacity: hasValidDocumentNumber ? 0.5 : 1,
              }}
              inputFormat="MM/DD/YYYY"
              clearable={false}
              value={dates}
              onChange={handleDateRangeChange}
              disabled={hasValidDocumentNumber}
              allowSingleDateInRange
              size="sm"
            />
          )}

          {isMobile && (
            <div style={{ width: "100%", display: "grid", gap: "0.75em" }}>
              {renderDocumentNumberFilter(true)}
              <Button
                variant="outline"
                color="cap-blue"
                fullWidth
                size="sm"
                leftIcon={<Filter size={16} />}
                aria-label={activeFiltersCount > 0 ? `Show filters (${activeFiltersCount})` : "Show filters"}
                onClick={() => setMobileFiltersOpen(true)}
              >
                Filters
                {activeFiltersCount > 0 && (
                  <Badge
                    size="xs"
                    ml={8}
                    color="cap-blue"
                    variant="filled"
                    styles={{
                      root: {
                        minWidth: 20,
                        height: 20,
                        padding: "0 6px",
                        borderRadius: 10,
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "none",
                        lineHeight: 1,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      },
                      inner: {
                        lineHeight: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      },
                    }}
                  >
                    {activeFiltersCount}
                  </Badge>
                )}
              </Button>
              <Drawer
                opened={mobileFiltersOpen}
                onClose={() => setMobileFiltersOpen(false)}
                position="bottom"
                size="auto"
                title={
                  <Text weight={600} size="md">
                    Filters {activeFiltersCount > 0 ? `(${activeFiltersCount})` : ""}
                  </Text>
                }
                padding="md"
                styles={{
                  drawer: {
                    borderTopLeftRadius: "12px",
                    borderTopRightRadius: "12px",
                    maxHeight: "80vh",
                    overflowY: "auto",
                  },
                }}
              >
                <div style={{ display: "grid", gap: "0.75em", paddingBottom: "max(1.5em, env(safe-area-inset-bottom))" }}>
                  <DateRangePicker
                    label="Date range"
                    placeholder="Date range"
                    amountOfMonths={1}
                    dropdownPosition="bottom"
                    style={{
                      width: "100%",
                      opacity: hasValidDocumentNumber ? 0.5 : 1,
                    }}
                    inputFormat="MM/DD/YYYY"
                    clearable={false}
                    value={dates}
                    onChange={handleDateRangeChange}
                    disabled={hasValidDocumentNumber}
                    allowSingleDateInRange
                    size={isMobile ? "md" : "sm"}
                  />
                  <DateRangeQuickPills
                    value={dates}
                    onChange={handleDateRangeChange}
                    disabled={hasValidDocumentNumber}
                  />
                  {renderSecondaryFilters(true)}
                  {hasValidDocumentNumber && documents.length > 0 && (
                    <Button
                      color="cap-blue"
                      variant="light"
                      size="sm"
                      fullWidth
                      onClick={handleAdjustDateRange}
                    >
                      Align date range
                    </Button>
                  )}
                  <Button color="cap-blue" fullWidth onClick={() => setMobileFiltersOpen(false)}>
                    Apply Filters
                  </Button>
                </div>
              </Drawer>
            </div>
          )}
        </div>

        {!isMobile && (documentFilterConfig || secondaryFilterConfig.length > 0) && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "0.6rem",
              marginBottom: "0.75rem",
              padding: "0.4rem 0.6rem",
              backgroundColor: "#f8f9fa",
              borderRadius: "6px",
              border: "1px solid #e9ecef",
            }}
          >
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
              {renderDocumentNumberFilter(false)}
              {renderSecondaryFilters(false)}
              {hasValidDocumentNumber && documents.length > 0 && (
                <Button
                  color="cap-blue"
                  variant="outline"
                  size="sm"
                  onClick={handleAdjustDateRange}
                >
                  Align date range
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "auto",
          paddingRight: "4px",
        }}
      >
        {loading && documents.length === 0 && <Loader color="cap-navy" style={{ margin: "2em auto", display: "block" }} />}
        {!loading && documents.length === 0 && doneInitialFetch && (
          <Alert title="No entries found" color="yellow" style={{ marginTop: "1em" }}>
            Please select a different date range or adjust your filters.
          </Alert>
        )}
        {documents && documents?.length > 0 && (
          <DataEntryTable
            elements={documents}
            setDocuments={setDocuments}
            config={displayConfig}
            templateName={templateName}
            companyId={companyId}
            selectedEntryId={selectedEntryId}
            entryMode={entryMode}
            onOpenEntry={openOverlay}
            onCloseEntry={closeOverlay}
            onResolvedEntry={handleResolvedEntry}
          ></DataEntryTable>
        )}
        {documents && documents.length > 0 && (
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: "1.5rem",
              marginTop: "2em",
              marginBottom: "1em",
            }}
          >
            {!paginationDone && (
              loading && documents.length > 0 ? (
                <Loader color="cap-navy" />
              ) : (
                <Button
                  color="cap-navy"
                  size={isMobile ? "md" : "sm"}
                  style={{
                    minWidth: isMobile ? "100%" : "160px",
                    minHeight: isMobile ? 44 : undefined,
                    height: isMobile ? 44 : undefined,
                  }}
                  onClick={() => loadMoreEntries(false)}
                  disabled={loading}
                >
                  Load more entries
                </Button>
              )
            )}
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                width: isMobile ? "100%" : "auto",
                justifyContent: isMobile ? "center" : undefined,
              }}
            >
              <Text size={isMobile ? "sm" : "xs"} color="dimmed" weight={500}>
                Page size:
              </Text>
              <SegmentedControl
                size={isMobile ? "md" : "xs"}
                value={String(pageSize)}
                onChange={(val) => handlePageSizeChange(Number(val))}
                data={[
                  { label: "20", value: "20" },
                  { label: "50", value: "50" },
                  { label: "100", value: "100" },
                ]}
                styles={{
                  root: {
                    minHeight: isMobile ? 44 : undefined,
                  },
                  control: {
                    minHeight: isMobile ? 44 : undefined,
                    height: isMobile ? 44 : undefined,
                  },
                  label: {
                    minHeight: isMobile ? 44 : undefined,
                    height: isMobile ? 44 : undefined,
                    display: isMobile ? "flex" : undefined,
                    alignItems: isMobile ? "center" : undefined,
                    justifyContent: isMobile ? "center" : undefined,
                    paddingLeft: isMobile ? 14 : undefined,
                    paddingRight: isMobile ? 14 : undefined,
                  },
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
